import { sections as sectionsUtils } from '#packages/util';

import { MigrationOrigin } from '../constants';
import { runSectionMigration } from './runSectionMigration';
import { MigrationFlow, PageMigrationStatus } from '../types';
import { resolveMigration } from './skipMigration';
import { isEmptyPage } from '../utils';

import type { CompRef } from 'types/documentServices';
import type { SectionsMigrationScope as Scope } from '../scope';

export const resolveMigrationFlow = (scope: Scope): MigrationFlow => {
  const { editorParamsAPI, editorAPI } = scope;
  const { sectionsMigrationFlow } = editorParamsAPI;

  const isSectionsEnabled =
    sectionsUtils.isSectionsEnabled() ||
    sectionsUtils.getSiteSectionsEditorData(editorAPI)?.isSectionsEnabled;

  if (!isSectionsEnabled && sectionsMigrationFlow) {
    // perPageEditor || perPageADI
    return sectionsMigrationFlow;
  }

  if (isSectionsEnabled) {
    // site that previously was ADI, after home page was migrated (isSectionsEnabled === true), is no longer ADI
    // all other non-migrated pages are on Editor 1.4, so the migrationFlow should be perPageEditor
    return MigrationFlow.PerPageEditor;
  }
};

export const runPerPageMigration = async (
  scope: Scope,
  pageId: string,
  options: { origin: MigrationOrigin },
) => {
  const { editorAPI } = scope;
  const { origin } = options;

  const migrationFlow = resolveMigrationFlow(scope);

  if (!migrationFlow) {
    scope.editorCoreAPI.hooks.sectionsMigrationReady.resolve({
      isMigrated: false,
    });
    return;
  }

  const isMobileEditor = editorAPI.isMobileEditor();
  const currentPage = isMobileEditor
    ? [{ id: pageId, type: 'DESKTOP' } as CompRef]
    : [editorAPI.components.get.byId(pageId)];

  await runSectionMigration(scope, {
    origin,
    flow: migrationFlow,
    pagesRefs: currentPage,
  });

  return {
    status: PageMigrationStatus.SUCCESS,
  };
};

export const resolveEmptyPageMigration = (
  scope: Scope,
  origin: MigrationOrigin,
  pageId: string,
) => {
  if (isEmptyPage(scope.editorAPI.documentServices, pageId)) {
    const migrationFlow = resolveMigrationFlow(scope);
    const currentPage = [scope.editorAPI.components.get.byId(pageId)];

    resolveMigration(scope, {
      flow: migrationFlow,
      origin,
      pagesRefs: currentPage,
    });

    return true;
  }

  return false;
};
